import { useSyncExternalStore } from 'react';
import { FEATURES, FEATURE_MAP } from './featureRegistry';
import { activePreset, PRESET_DEFINITIONS } from './selectors';

const STORAGE_KEY = 'te_admin_v2';
const LEGACY_STORAGE_KEY = 'te_admin_v1';
const SCHEMA_VERSION = 2;

/**
 * Generate default feature configurations based on featureRegistry
 */
function getDefaultFeatures() {
  const map = {};
  for (const f of FEATURES) {
    map[f.id] = {
      enabled: f.defaultEnabled ?? true,
      mode: f.defaultMode || 'live',
      access: f.defaultAccess || (f.writable ? 'readwrite' : 'readonly')
    };
  }
  return map;
}

const DEFAULT_STATE = {
  version: SCHEMA_VERSION,
  killSwitch: false,
  readOnlyAll: false,
  offline: false,
  latencyMs: 0,
  failRate: 0,
  density: 'comfortable',
  fontScale: '100',
  reduceMotion: false,
  demoRibbon: true,
  bannerText: '',
  bannerActive: false,
  brandOverride: null, // { productName, schoolName, tagline, logoText, accentColor }
  hiddenMenus: {
    principal: [],
    teacher: [],
    student: [],
    parent: []
  },
  activePreset: 'All live',
  features: getDefaultFeatures(),
  changeLog: [
    {
      id: 'init',
      timestamp: new Date().toISOString(),
      action: 'System initialized',
      details: 'Admin console store initialized with standard defaults'
    }
  ],
  demoChecklist: {
    step_gate: false,
    step_parent: false,
    step_punch: false,
    step_feed: false,
    step_refresh: false,
    step_preset: false
  }
};

/**
 * Safe state normalization & migration from older versions
 */
function normalizeState(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return DEFAULT_STATE;
  }

  const features = { ...getDefaultFeatures() };
  if (parsed.features && typeof parsed.features === 'object' && !Array.isArray(parsed.features)) {
    for (const [id, cfg] of Object.entries(parsed.features)) {
      if (features[id] && cfg && typeof cfg === 'object') {
        features[id] = {
          ...features[id],
          enabled: typeof cfg.enabled === 'boolean' ? cfg.enabled : features[id].enabled,
          mode: typeof cfg.mode === 'string' ? cfg.mode : features[id].mode,
          access: typeof cfg.access === 'string' ? cfg.access : features[id].access
        };
      }
    }
  }

  const base = {
    ...DEFAULT_STATE,
    ...parsed,
    version: SCHEMA_VERSION,
    features,
    hiddenMenus: {
      principal: Array.isArray(parsed.hiddenMenus?.principal) ? parsed.hiddenMenus.principal : [],
      teacher: Array.isArray(parsed.hiddenMenus?.teacher) ? parsed.hiddenMenus.teacher : [],
      student: Array.isArray(parsed.hiddenMenus?.student) ? parsed.hiddenMenus.student : [],
      parent: Array.isArray(parsed.hiddenMenus?.parent) ? parsed.hiddenMenus.parent : []
    },
    demoChecklist: {
      ...DEFAULT_STATE.demoChecklist,
      ...(parsed.demoChecklist && typeof parsed.demoChecklist === 'object' ? parsed.demoChecklist : {})
    }
  };

  base.activePreset = activePreset(base);
  return base;
}

/**
 * Load store from localStorage safely with migration
 */
function loadStore() {
  try {
    if (typeof localStorage === 'undefined') return DEFAULT_STATE;

    let raw = localStorage.getItem(STORAGE_KEY);
    let isLegacy = false;

    // Check for legacy v1 store if v2 does not exist
    if (!raw) {
      raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (raw) isLegacy = true;
    }

    if (!raw) return DEFAULT_STATE;

    const parsed = JSON.parse(raw);
    const normalized = normalizeState(parsed);

    // If migrated from legacy v1, persist to v2 and purge legacy key
    if (isLegacy) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch (e) {}
    }

    return normalized;
  } catch (err) {
    console.error('[AdminStore] Corrupt or unparseable stored state. Falling back to defaults.', err);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('app:toast', {
          detail: { message: 'Stored admin configuration was corrupt or incompatible; restored to defaults.', type: 'warning' }
        })
      );
    }
    return DEFAULT_STATE;
  }
}

let currentState = loadStore();
const listeners = new Set();

function emitChange(persist = true) {
  currentState.activePreset = activePreset(currentState);

  if (persist && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(currentState));
    } catch (err) {
      console.error('[AdminStore] Failed to persist state:', err);
    }
  }
  applyDocumentStyles(currentState);
  listeners.forEach((listener) => listener());
}

/**
 * Returns current store state snapshot synchronously
 */
export function getAdminState() {
  return currentState;
}

/**
 * Reset all local storage, session storage, and reload application
 */
export function resetAllLocalSettings() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      localStorage.removeItem('user');
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
  } catch (err) {
    console.error('Failed clearing local settings:', err);
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
}

/**
 * Cross-tab & cross-window synchronization via 'storage' event
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      try {
        if (!e.newValue) {
          currentState = DEFAULT_STATE;
        } else {
          const parsed = JSON.parse(e.newValue);
          currentState = normalizeState(parsed);
        }
        applyDocumentStyles(currentState);
        listeners.forEach((listener) => listener());
      } catch (err) {
        console.error('[AdminStore] Cross-tab sync parse error:', err);
      }
    }
  });
}

/**
 * Apply global HTML element attributes and styles according to admin state
 */
export function applyDocumentStyles(state) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Density class
  if (state.density === 'compact') {
    root.classList.add('theme-compact');
  } else {
    root.classList.remove('theme-compact');
  }

  // Motion class
  if (state.reduceMotion) {
    root.classList.add('reduce-motion');
  } else {
    root.classList.remove('reduce-motion');
  }

  // Font scale class
  root.classList.remove('font-scale-90', 'font-scale-110');
  if (state.fontScale === '90') {
    root.classList.add('font-scale-90');
  } else if (state.fontScale === '110') {
    root.classList.add('font-scale-110');
  }

  // Brand accent color
  if (state.brandOverride?.accentColor) {
    root.style.setProperty('--accent', state.brandOverride.accentColor);
  } else {
    root.style.removeProperty('--accent');
  }
}

// Initial styles application
if (typeof document !== 'undefined') {
  applyDocumentStyles(currentState);
}

/**
 * Add a record to change log (capped at 100 entries)
 */
export function logChange(action, details) {
  const newEntry = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    timestamp: new Date().toISOString(),
    action,
    details
  };
  const updatedLog = [newEntry, ...currentState.changeLog].slice(0, 100);
  currentState = { ...currentState, changeLog: updatedLog };
}

/**
 * Hook to subscribe to admin store
 */
export function useAdminStore() {
  const state = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => currentState,
    () => DEFAULT_STATE
  );
  return state;
}

/* ==========================================================================
   NAMED STORE MUTATION ACTIONS (Single source of truth)
   ========================================================================== */

export function setMode(featureId, mode) {
  const feat = currentState.features[featureId] || { enabled: true, mode: 'live', access: 'readwrite' };
  currentState = {
    ...currentState,
    features: {
      ...currentState.features,
      [featureId]: { ...feat, mode }
    }
  };
  logChange(`Mode changed: ${featureId}`, `Set mode to ${mode.toUpperCase()}`);
  emitChange();
}

export function setAccess(featureId, access) {
  const feat = currentState.features[featureId] || { enabled: true, mode: 'live', access: 'readwrite' };
  currentState = {
    ...currentState,
    features: {
      ...currentState.features,
      [featureId]: { ...feat, access }
    }
  };
  logChange(`Access changed: ${featureId}`, `Set access to ${access.toUpperCase()}`);
  emitChange();
}

export function setEnabled(featureId, enabled) {
  const feat = currentState.features[featureId] || { enabled: true, mode: 'live', access: 'readwrite' };
  currentState = {
    ...currentState,
    features: {
      ...currentState.features,
      [featureId]: { ...feat, enabled }
    }
  };
  logChange(`Feature toggled: ${featureId}`, enabled ? 'Enabled' : 'Disabled');
  emitChange();
}

export function setKillSwitch(killSwitch) {
  currentState = { ...currentState, killSwitch: Boolean(killSwitch) };
  logChange('API Kill Switch', killSwitch ? 'Kill switch activated (all API blocked)' : 'Kill switch deactivated');
  emitChange();
}

export function setReadOnlyAll(readOnlyAll) {
  currentState = { ...currentState, readOnlyAll: Boolean(readOnlyAll) };
  logChange('Global Read-Only', readOnlyAll ? 'Global read-only activated' : 'Global read-only deactivated');
  emitChange();
}

export function setOffline(offline) {
  currentState = { ...currentState, offline: Boolean(offline) };
  logChange('Offline Mode', offline ? 'Simulated offline enabled' : 'Simulated offline disabled');
  emitChange();
}

export function setLatency(latencyMs) {
  currentState = { ...currentState, latencyMs: Number(latencyMs) || 0 };
  logChange('Simulated Latency', `${latencyMs}ms delay injected`);
  emitChange();
}

export function setFailRate(failRate) {
  currentState = { ...currentState, failRate: Number(failRate) || 0 };
  logChange('Simulated Failure Rate', `${failRate}% failure rate`);
  emitChange();
}

export function setBanner(text, active) {
  currentState = { ...currentState, bannerText: text, bannerActive: Boolean(active) };
  logChange('Emergency Announcement Banner', active ? `Active: "${text}"` : 'Deactivated');
  emitChange();
}

export function setBrand(brandOverride) {
  currentState = { ...currentState, brandOverride };
  logChange('Brand Customization', brandOverride ? `Updated (${brandOverride.schoolName || brandOverride.productName})` : 'Reset to defaults');
  emitChange();
}

export function setDisplay(displayConfig) {
  currentState = { ...currentState, ...displayConfig };
  logChange('Display Settings', Object.keys(displayConfig).join(', '));
  emitChange();
}

export function toggleMenuItem(role, path) {
  const currentList = currentState.hiddenMenus[role] || [];
  const isHidden = currentList.includes(path);
  const updated = isHidden ? currentList.filter((p) => p !== path) : [...currentList, path];

  currentState = {
    ...currentState,
    hiddenMenus: {
      ...currentState.hiddenMenus,
      [role]: updated
    }
  };
  logChange(`Menu visibility (${role})`, `${path} -> ${isHidden ? 'visible' : 'hidden'}`);
  emitChange();
}

export function applyPreset(name) {
  let newFeatures = {};
  for (const f of FEATURES) {
    newFeatures[f.id] = {
      ...(currentState.features?.[f.id] || {}),
      enabled: true
    };
  }
  let newKill = false;
  let newReadOnly = false;

  switch (name) {
    case 'All live': {
      newKill = false;
      newReadOnly = false;
      for (const f of FEATURES) {
        newFeatures[f.id] = {
          ...newFeatures[f.id],
          enabled: true,
          mode: 'live',
          access: f.writable ? 'readwrite' : 'readonly'
        };
      }
      break;
    }

    case 'Exhibition': {
      newKill = false;
      newReadOnly = false;
      const liveSet = PRESET_DEFINITIONS['Exhibition']?.liveSet || new Set([
        'attendance_today', 'live_feed', 'chat', 'notifications_bell', 'simulate_punch', 'gate_display'
      ]);
      for (const f of FEATURES) {
        const isLive = liveSet.has(f.id);
        newFeatures[f.id] = {
          ...newFeatures[f.id],
          enabled: true,
          mode: isLive ? 'live' : 'demo',
          access: f.writable ? 'readwrite' : 'readonly'
        };
      }
      break;
    }

    case 'All demo / offline': {
      newKill = true;
      newReadOnly = false;
      for (const f of FEATURES) {
        newFeatures[f.id] = {
          ...newFeatures[f.id],
          enabled: true,
          mode: 'demo'
        };
      }
      break;
    }

    case 'Safe (read-only everywhere)': {
      newKill = false;
      newReadOnly = true;
      for (const f of FEATURES) {
        newFeatures[f.id] = {
          ...newFeatures[f.id],
          access: 'readonly'
        };
      }
      break;
    }

    default:
      return;
  }

  currentState = {
    ...currentState,
    killSwitch: newKill,
    readOnlyAll: newReadOnly,
    features: newFeatures
  };
  logChange('Applied preset', name);
  emitChange();
}

export function setGlobalConfig(partial) {
  currentState = { ...currentState, ...partial };
  logChange('Updated global config', Object.keys(partial).join(', '));
  emitChange();
}

export function setFeatureConfig(featureId, partial) {
  const currentFeat = currentState.features[featureId] || {
    enabled: true,
    mode: 'live',
    access: 'readwrite'
  };
  currentState = {
    ...currentState,
    features: {
      ...currentState.features,
      [featureId]: { ...currentFeat, ...partial }
    }
  };
  logChange(`Updated feature: ${featureId}`, JSON.stringify(partial));
  emitChange();
}

export function resetAll() {
  currentState = {
    ...DEFAULT_STATE,
    changeLog: [
      {
        id: `reset-${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: 'Reset all settings',
        details: 'All admin flags, presets, and overrides restored to factory defaults'
      }
    ]
  };
  emitChange();
}

export function exportSettings() {
  return JSON.stringify(currentState, null, 2);
}

export function importSettings(jsonStr) {
  try {
    const parsed = JSON.parse(jsonStr);
    if (!parsed || typeof parsed !== 'object') throw new Error('Invalid JSON format');
    currentState = normalizeState(parsed);
    logChange('Imported settings', `Imported ${Object.keys(parsed).length} configuration keys`);
    emitChange();
    return true;
  } catch (err) {
    console.error('[AdminStore] Import error:', err);
    return false;
  }
}

export function toggleChecklistStep(stepId) {
  const currentVal = !!currentState.demoChecklist[stepId];
  currentState = {
    ...currentState,
    demoChecklist: {
      ...currentState.demoChecklist,
      [stepId]: !currentVal
    }
  };
  emitChange();
}

export function clearChangeLog() {
  currentState = { ...currentState, changeLog: [] };
  emitChange();
}

/* ==========================================================================
   READ-ONLY CONVENIENCE HELPERS
   ========================================================================== */

export function useFeature(id) {
  const store = useAdminStore();
  const feat = store.features?.[id] || { enabled: true, mode: 'live', access: 'readwrite' };
  const globalReadOnly = store.readOnlyAll;
  const isReadOnly = globalReadOnly || feat.access === 'readonly';

  return {
    enabled: feat.enabled !== false,
    mode: feat.mode || 'live',
    access: feat.access || 'readwrite',
    readOnly: isReadOnly,
    isWritable: !isReadOnly && feat.enabled !== false
  };
}

export function getDataMode(id) {
  return currentState.features?.[id]?.mode || 'live';
}

export function isFeatureReadOnly(id) {
  if (currentState.readOnlyAll) return true;
  return currentState.features?.[id]?.access === 'readonly';
}

export function isFeatureEnabled(id) {
  return currentState.features?.[id]?.enabled !== false;
}
